(() => {
  if (window.retailDeluxeBannerVideo) {
    window.retailDeluxeBannerVideo.mount(document);
    return;
  }

  const bannerSelector = '[data-retail-deluxe-banner]';
  const videoStates = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const getBanners = (scope) => {
    const banners = [];

    if (scope instanceof Element && scope.matches(bannerSelector)) {
      banners.push(scope);
    }

    if (scope.querySelectorAll) {
      banners.push(...scope.querySelectorAll(bannerSelector));
    }

    return banners;
  };

  const promoteVideoLoading = (video, state) => {
    if (state.preloadRequested) return;

    state.preloadRequested = true;
    video.preload = 'auto';
    video.setAttribute('loading', 'eager');
    video.setAttribute('fetchpriority', 'high');
    video.load();
  };

  const requestPlayback = (video, state) => {
    if (!state.inView || state.playPending || video.ended) return;

    promoteVideoLoading(video, state);
    state.playPending = true;

    const playPromise = video.play();
    if (!playPromise) {
      state.playPending = false;
      return;
    }

    playPromise
      .then(() => {
        state.playPending = false;
        state.playRetries = 0;
      })
      .catch(() => {
        state.playPending = false;
        if (!state.inView || video.ended || state.playRetries >= 2) return;

        state.playRetries += 1;
        const retry = () => requestPlayback(video, state);

        if (video.readyState >= 2) {
          window.requestAnimationFrame(retry);
        } else {
          video.addEventListener('canplay', retry, { once: true });
        }
      });
  };

  const initializeVideo = (video) => {
    if (videoStates.has(video)) return;

    video.autoplay = false;
    video.loop = false;
    video.pause();

    const state = {
      inView: false,
      playPending: false,
      playRetries: 0,
      preloadRequested: false,
      playbackObserver: null,
    };
    videoStates.set(video, state);

    if (reducedMotion.matches) return;

    if (window.getComputedStyle(video).display !== 'none') {
      promoteVideoLoading(video, state);
    }

    if (!('IntersectionObserver' in window)) return;

    state.playbackObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          state.inView = entry.isIntersecting;

          if (video.ended) {
            state.playbackObserver.disconnect();
            state.playbackObserver = null;
            return;
          }

          if (entry.isIntersecting) {
            requestPlayback(video, state);
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.25 }
    );

    video.addEventListener(
      'ended',
      () => {
        state.inView = false;
        if (state.playbackObserver) state.playbackObserver.disconnect();
        state.playbackObserver = null;
      },
      { once: true }
    );

    state.playbackObserver.observe(video);
  };

  const mount = (scope = document) => {
    getBanners(scope).forEach((banner) => {
      banner.querySelectorAll('video').forEach(initializeVideo);
    });
  };

  const unmount = (scope) => {
    getBanners(scope).forEach((banner) => {
      banner.querySelectorAll('video').forEach((video) => {
        const state = videoStates.get(video);
        if (state?.playbackObserver) state.playbackObserver.disconnect();
        videoStates.delete(video);
        video.pause();
      });
    });
  };

  window.retailDeluxeBannerVideo = { mount, unmount };

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => unmount(event.target));

  mount(document);
})();
